import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { ThemeProvider } from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Flex } from "@similarweb/ui-components/foundations";

import ChatMessage from "app/pages/sales-intelligence/modules/ai-agent/components/ChatMessage/ChatMessage";
import { CHAT_THEME } from "app/pages/sales-intelligence/modules/ai-agent/components/MessagesList/styles";
import { useGetCompanyInfo } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetCompanyInfo";
import { useGetThreadById } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetThreadById";
import { getMeetingAgentUiContext } from "app/pages/sales-intelligence/pages/ai-agent/components/AIAgentMessagesList/AIAgentMessagesList";

import { stripEmojis } from "../../utils";

import { StyledPDFBackground, StyledPDFContent } from "./styles";

export const AIAgentChatPrint = () => {
    const uiContext = getMeetingAgentUiContext();
    const theme = CHAT_THEME[uiContext.chatTheme];

    const {
        params: { id: threadId, messageId, domain },
    } = useCurrentStateAndParams<{ messageId?: string; id?: string; domain?: string }>();

    const { data: historyData, isLoading: isHistoryDataLoading } = useGetThreadById({ threadId, enabled: true });
    const { data: companyInfo } = useGetCompanyInfo(domain ?? "");

    const message = useMemo(() => {
        return historyData?.messages.find((msg) => msg.id === messageId);
    }, [historyData, messageId]);

    const mdComponentProps = useMemo(
        () => ({
            companyLogoSrc: companyInfo?.favicon ?? "",
            disableStructuredBrief: true,
        }),
        [companyInfo?.favicon],
    );

    const sanitizedContent = useMemo(() => stripEmojis(message?.content ?? ""), [message?.content]);

    if (!threadId || !messageId || isHistoryDataLoading || !message) {
        return null;
    }

    return (
        <>
            <StyledPDFBackground />
            <StyledPDFContent>
                <Flex
                    width="100%"
                    height="100%"
                    justifyContent="center"
                    margin="0 0 60px"
                >
                    <ThemeProvider theme={theme}>
                        <ChatMessage
                            variant="ai"
                            content={sanitizedContent}
                            messageId={message.id}
                            mdComponentProps={mdComponentProps}
                            markdownComponent={uiContext.markdownComponent}
                            withControls={false}
                        />
                    </ThemeProvider>
                </Flex>
            </StyledPDFContent>
        </>
    );
};
